import { useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
const SidebarAd = lazy(() => import('@/components/SidebarAd').then(m => ({ default: m.SidebarAd })));

import { Leaderboard } from '@/components/Leaderboard';
import { SEOHead } from '@/components/SEOHead';
import { StructuredData, buildWebsiteSchema, buildProductListSchema, buildSiteNavigationSchema } from '@/components/StructuredData';
import { useProducts } from '@/hooks/useProducts';
import { useRecordPageView } from '@/hooks/usePageViews';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const Index = () => {
  const { data: products } = useProducts();
  const [searchQuery, setSearchQuery] = useState('');
  useRecordPageView();

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Best ROI Software Tools & ROI Tracking Tools — Compare Rankings | TrustROI"
        description="TrustROI ranks the best ROI software, ROI tracking tools, and marketing ROI software by verified data. Compare ROI CRM, project management, and automation tools — find the highest-return software online."
        canonical=""
      />

      <StructuredData data={buildWebsiteSchema()} />
      <StructuredData data={buildSiteNavigationSchema()} />
      {products && <StructuredData data={buildProductListSchema(products)} />}
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is the best ROI tracking software?',
            acceptedAnswer: { '@type': 'Answer', text: 'TrustROI ranks 130+ software tools by verified ROI using payment-gateway data from Stripe, Paddle, and RevenueCat. Compare ROI scores, verified user counts, and retention across CRM, marketing, project management, and more.' },
          },
          {
            '@type': 'Question',
            name: 'What is an ROI CRM and which CRM has the best ROI?',
            acceptedAnswer: { '@type': 'Answer', text: 'An ROI CRM delivers measurable return on investment through verified customer retention. On TrustROI, top CRM tools are ranked by real paying customers and retention months — not reviews. Compare HubSpot, Freshsales, Pipedrive, and more by verified ROI.' },
          },
          {
            '@type': 'Question',
            name: 'How do I compare ROI optimization solutions online?',
            acceptedAnswer: { '@type': 'Answer', text: 'TrustROI lets you compare ROI across 130+ tools using the formula: (Verified Paying Customers × Avg Retention Months) / Monthly Price. Every score is backed by payment-gateway-verified data — no reviews, no upvotes.' },
          },
          {
            '@type': 'Question',
            name: 'What are the best ROI tracking tools for marketing?',
            acceptedAnswer: { '@type': 'Answer', text: 'TrustROI ranks marketing ROI software like Klaviyo, Omnisend, ActiveCampaign, and MailerLite by verified retention and customer data. See which marketing tools deliver the highest return on every dollar spent.' },
          },
          {
            '@type': 'Question',
            name: 'Is TrustROI free to use?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. Browsing, comparing tools, and viewing verified ROI data is completely free. Software founders can list their tools for free and connect their payment gateway for verification.' },
          },
        ],
      }} />

      <Header />

      <section className="py-4 md:py-5 text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-foreground text-xl sm:text-2xl md:text-4xl max-w-4xl mx-auto font-display font-bold leading-tight">
            See Which Software Tools Are Actually Worth Your Credit Card
          </h1>
          <Link to="/trustwall" className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-gradient-to-r from-muted/80 to-muted/40 border border-border hover:border-primary/30 transition-all group">
            <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-1.5 py-0.5 rounded">New</span>
            <span className="text-xs sm:text-sm text-muted-foreground group-hover:text-foreground transition-colors">
              TrustWall — Verified testimonials powered by real payment data
            </span>
            <span className="text-muted-foreground group-hover:text-foreground transition-colors">→</span>
          </Link>
        </div>
      </section>

      <div className="container mx-auto px-4 py-4 flex gap-6">
        <Suspense fallback={null}><SidebarAd side="left" /></Suspense>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tools..."
                className="pl-10 bg-secondary border-border h-9 text-sm"
              />
            </div>
            <Link to="/add">
              <Button size="sm" className="gap-1.5 font-semibold whitespace-nowrap h-9">
                <Plus className="h-4 w-4" />
                Add your tool
              </Button>
            </Link>
          </div>
          <Leaderboard searchQuery={searchQuery} />
        </div>
        <Suspense fallback={null}><SidebarAd side="right" /></Suspense>
      </div>

      <Footer />
    </div>
  );
};

export default Index;
